import {Alert, Group, SegmentedControl, Stack, Text, Textarea, TextInput} from '@mantine/core'
import {IconAlertTriangle, IconMapPin} from '@tabler/icons-react'
import {useEffect, useState} from 'react'

import {useCustomerSavedLocations} from 'src/customers/saved-locations/use-saved-locations'

import {CoordinateInput} from './coordinate-input'
import type {GoogleMapsSource} from './google-maps/google-maps-metrics'
import {
    type GoogleMapsProviderStatus,
    useGoogleMapsProviderStatus,
} from './google-maps/google-maps-provider'
import {PlacesAutocompleteInput} from './google-maps/places-autocomplete-input'
import {LocationMapPicker} from './location-map-picker'
import {SaveLocationCheckbox} from './save-location-checkbox'
import {SavedLocationSelector} from './saved-location-selector'
import type {JobLocation, LocationPositionMode} from './types'

const POSITION_MODE_OPTIONS = [
    {label: 'Address', value: 'address'},
    {label: 'Lat/Long', value: 'latlng'},
    {label: 'Custom pin', value: 'pin'},
]

type LocationEditMode = 'create-new' | 'select-saved' | 'edit'

type LocationEditFormProps = {
    location: JobLocation
    source: GoogleMapsSource
    mode: LocationEditMode
    customerId: string | null
    nameError?: string
    latitudeError?: string
    onUpdate: (partial: Partial<JobLocation>) => void
    onNameBlur?: () => void
    onPositionBlur?: () => void
    MapPickerComponent?: typeof LocationMapPicker
    addressAutocompleteKey?: string
    showSaveCheckbox?: boolean
    nameLabel?: string
    showNotes?: boolean
    enableProviderFallback?: boolean
    mapProviderStatus?: GoogleMapsProviderStatus
}

export function LocationEditForm({
    location,
    source,
    mode,
    customerId,
    nameError,
    latitudeError,
    onUpdate,
    onNameBlur,
    onPositionBlur,
    MapPickerComponent = LocationMapPicker,
    addressAutocompleteKey,
    showSaveCheckbox = true,
    nameLabel = 'Location name',
    showNotes = true,
    enableProviderFallback = false,
    mapProviderStatus: mapProviderStatusOverride,
}: LocationEditFormProps) {
    const {data: savedLocations = []} = useCustomerSavedLocations(customerId ?? '')
    const contextProviderStatus = useGoogleMapsProviderStatus()
    const mapProviderStatus = mapProviderStatusOverride ?? contextProviderStatus
    const [mapRuntimeFailed, setMapRuntimeFailed] = useState(false)
    const providerUnavailable =
        mapRuntimeFailed ||
        (enableProviderFallback &&
            (mapProviderStatus === 'missing' ||
                mapProviderStatus === 'script-load-failed' ||
                mapProviderStatus === 'authentication-failed'))
    const [addressLookupError, setAddressLookupError] = useState<string | null>(null)
    const positionMode = providerUnavailable ? 'latlng' : location.positionMode

    useEffect(() => {
        if (
            providerUnavailable &&
            !location.fromSavedLocationId &&
            location.positionMode !== 'latlng'
        ) {
            onUpdate({positionMode: 'latlng'})
        }
    }, [location.positionMode, location.fromSavedLocationId, onUpdate, providerUnavailable])

    function handlePositionChange(position: {latitude: number; longitude: number}) {
        // Don't allow position change when using a saved location
        if (location.fromSavedLocationId) {
            return
        }
        onUpdate({
            latitude: position.latitude,
            longitude: position.longitude,
            positionMode: 'pin',
            addressText: null,
        })
    }

    function handleSavedLocationSelect(savedLocationId: string | null) {
        // Pre-fill form when a saved location is selected
        if (savedLocationId) {
            const selectedLocation = savedLocations.find((l) => l.id === savedLocationId)
            if (selectedLocation) {
                onUpdate({
                    fromSavedLocationId: savedLocationId,
                    rememberAsSavedLocation: location.rememberAsSavedLocation,
                    name: selectedLocation.name,
                    notes: selectedLocation.notes ?? '',
                    latitude: selectedLocation.latitude,
                    longitude: selectedLocation.longitude,
                })
                return
            }
        }

        onUpdate({fromSavedLocationId: savedLocationId})
    }

    function handleSaveAsSavedLocationChange(checked: boolean) {
        onUpdate({rememberAsSavedLocation: checked})
    }

    return (
        <Group align="flex-start" wrap="nowrap" gap="md" grow>
            <Stack gap="md">
                {mode === 'select-saved' && customerId && (
                    <>
                        {savedLocations.length === 0 ? (
                            <Text c="orange" size="sm">
                                <IconAlertTriangle size={16} style={{verticalAlign: 'middle'}} /> No
                                saved locations available. Please create saved locations for this
                                customer first.
                            </Text>
                        ) : (
                            <SavedLocationSelector
                                customerId={customerId}
                                value={location.fromSavedLocationId}
                                onChange={handleSavedLocationSelect}
                            />
                        )}
                    </>
                )}

                {/* Show form fields when:
                   - In create-new mode, OR
                   - In edit mode, OR
                   - In select-saved mode AND a saved location is selected */}
                {(mode === 'create-new' || mode === 'edit' || location.fromSavedLocationId) && (
                    <>
                        <TextInput
                            label={nameLabel}
                            leftSection={<IconMapPin size={16} />}
                            value={location.name}
                            error={nameError}
                            onChange={(event) => onUpdate({name: event.currentTarget.value})}
                            onBlur={onNameBlur}
                        />

                        {showNotes && (
                            <Textarea
                                label="Notes"
                                description="Gate codes, delivery instructions, or other details for drivers"
                                autosize
                                minRows={3}
                                maxRows={10}
                                value={location.notes}
                                onChange={(event) => onUpdate({notes: event.currentTarget.value})}
                            />
                        )}

                        {(mode === 'create-new' ||
                            (mode === 'edit' && !location.fromSavedLocationId)) && (
                            <>
                                {providerUnavailable ? (
                                    <Alert color="orange">
                                        Map service unavailable. Enter latitude and longitude to
                                        continue.
                                    </Alert>
                                ) : (
                                    <SegmentedControl
                                        value={location.positionMode}
                                        data={POSITION_MODE_OPTIONS}
                                        onChange={(nextMode) => {
                                            setAddressLookupError(null)
                                            onUpdate({
                                                positionMode: nextMode as LocationPositionMode,
                                            })
                                        }}
                                    />
                                )}

                                <div onBlur={onPositionBlur}>
                                    {positionMode === 'address' && (
                                        <PlacesAutocompleteInput
                                            source={source}
                                            key={addressAutocompleteKey ?? 'location-address'}
                                            label="Address"
                                            error={addressLookupError ?? latitudeError}
                                            onChange={(address) => {
                                                setAddressLookupError(null)
                                                onUpdate({addressText: address})
                                            }}
                                            onLookupError={setAddressLookupError}
                                            onPlaceSelect={(place) => {
                                                setAddressLookupError(null)
                                                onUpdate({
                                                    latitude: place.latitude,
                                                    longitude: place.longitude,
                                                    addressText: place.address,
                                                })
                                            }}
                                        />
                                    )}

                                    {positionMode === 'latlng' && (
                                        <Group grow align="flex-start">
                                            <CoordinateInput
                                                label="Latitude"
                                                value={location.latitude}
                                                onChange={(latitude) => onUpdate({latitude})}
                                            />
                                            <CoordinateInput
                                                label="Longitude"
                                                value={location.longitude}
                                                onChange={(longitude) => onUpdate({longitude})}
                                            />
                                        </Group>
                                    )}
                                </div>

                                {positionMode === 'pin' && (
                                    <Text size="sm" c="dimmed">
                                        Approximate location — drivers navigate by the map pin.
                                    </Text>
                                )}
                            </>
                        )}

                        {showSaveCheckbox &&
                            (mode === 'create-new' || location.fromSavedLocationId) && (
                                <SaveLocationCheckbox
                                    checked={location.rememberAsSavedLocation}
                                    onChange={handleSaveAsSavedLocationChange}
                                />
                            )}

                        <MapPickerComponent
                            source={source}
                            latitude={location.latitude}
                            longitude={location.longitude}
                            onPositionChange={handlePositionChange}
                            height={320}
                            onError={() => setMapRuntimeFailed(true)}
                            hidden={providerUnavailable}
                        />

                        {latitudeError && positionMode !== 'address' && (
                            <Text size="sm" c="red">
                                {latitudeError}
                            </Text>
                        )}

                        {location.fromSavedLocationId && (
                            <Text size="sm" c="dimmed">
                                Coordinates are locked when using a saved location
                            </Text>
                        )}
                    </>
                )}
            </Stack>
        </Group>
    )
}
